import { Head, Link } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";

import { ActivationGuide } from "@/components/activation-guide";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface ComparisonFaq {
  question: string;
  answer: string;
}

interface BannerbearAlternativeProps {
  page_title?: string;
  faqs?: ComparisonFaq[];
}

interface ComparisonRow {
  tool: string;
  what: string;
  bestFor: string;
  generate: string;
  mcp: string;
  framework: string;
  freeTier: string;
  paid: string;
  limits: string;
}

const ROWS: ComparisonRow[] = [
  {
    tool: "OG Pilot",
    what: "Hosted Open Graph API and MCP server. Named templates return a 1200×630 card. There is no design editor in the docs.",
    bestFor: "A social card from a signed server call or from an agent that has a page URL.",
    generate:
      "POST https://ogpilot.com/api/v1/images with a JWT in the JSON field token. Templates include page, blog_post, podcast, product, event, book, portfolio, company, and github. MCP generate_og_image takes a page URL instead.",
    mcp: "https://ogpilot.com/mcp. Streamable HTTP. OAuth 2.1 with PKCE. generate_og_image needs an active plan and one credit. debug_open_graph, compress_image, and extract_color_palette work on a guest session.",
    framework:
      "SDKs for Ruby, JavaScript, Python, and PHP. Guides for Next.js, Rails, and Astro. A WordPress plugin connects with OAuth.",
    freeTier:
      "7-day trial, no card. The trial image quota in the app is 100 completed images. The API accepts a trial after DNS TXT verification. A trial is not enough for MCP generation.",
    paid: "Starter is $5/month or $50/year for up to 10,000 pages and 0 AI credits. Builder is $49/month or $99/year, 100,000 pages, 100 credits. Growth is $99/month or $199/year, unlimited pages, 200 credits. Extra credits are $9 per 30 ($0.30 each).",
    limits:
      "API: 600 requests/minute and 60,000/hour per API key. Response reports width 1200 and height 630. MCP generation: 10 new images/hour. No video or PDF API.",
  },
  {
    tool: "Bannerbear",
    what: "API and no-code platform for template images, videos, and PDFs. You design the template. The API fills layers.",
    bestFor: "Designed templates, plus video, PDF, batches, or workflows. A designer owns the layout.",
    generate:
      "POST /v5/images with a template UID and a modifications array. Auth header: Authorization: Bearer API_KEY. Official libraries: Node, Python, Ruby, PHP. Instant URLs render one template from the query string when the link is first requested.",
    mcp: "Hosted OAuth at https://mcp.bannerbear.com/ (30 tools), /workflows (8), and /all (63). Local option: npx @bannerbear/mcp with an API key. Listed on the Automate plan.",
    framework: "REST API and those four libraries. No framework-specific Open Graph guide was on the pages checked.",
    freeTier:
      "Trial with 30 API credits, no card. One workspace, three image templates, three animation templates, three workflows, and three instant URLs. No ongoing free plan is listed.",
    paid: "Automate $49/month: 1,000 API credits and 200 AI credits. Scale $149/month: 10,000 API credits and 600 AI credits. Enterprise $299/month: 50,000 API credits and 1,250 AI credits. Higher credit quantities are listed without a separate price in the fetched text.",
    limits:
      "60 POST requests per 10 seconds. One jpg at scale 1 is 1 credit. jpg and png at scale 2 is 4. AI features add 1 credit per layer. Out of credits, the request is refused. Instant URLs start on Scale, not Automate.",
  },
  {
    tool: "Placid",
    what: "Template automation for images, PDFs, and videos, with a drag-and-drop editor, a REST API, and a URL API.",
    bestFor: "A designed social card you can also reuse for PDF, video, or no-code tools.",
    generate:
      "REST: POST https://api.placid.app/api/rest/images with template_uuid and layers. URL API: https://api.placid.app/u/{template_uuid}?layer[property]=value. Placid's URL docs say to embed that link in og:image. 1 image = 1 credit up to a 4000 px canvas.",
    mcp: "Marked on Basic, Pro, Business, and VIP. Per-project URL on mcp.placid.app. Auth is the project API token, as a Bearer header or an api_token query parameter. Outputs stay template-driven.",
    framework:
      "REST API, URL API, and the integrations on the pricing table: WordPress, Webflow, Ghost, Zapier, Make, n8n, Airtable, and ChatGPT. Editor SDK and Mobile SDK are separate rows.",
    freeTier:
      "Pricing FAQ: a free trial, no card, and a few complimentary credits. The count is not on that page. The free OG generator says 5 watermark-free images per day on that tool, and 50 credits after signup. Test mode serves unlimited watermarked previews.",
    paid: "Not published. The pricing page lists credits only: Basic 500, Pro 2,500, Business 25,000, VIP 100,000 per month. No dollar amount is in the page HTML.",
    limits:
      "60 requests per minute. create_now: true allows 10 simultaneous creates. No overage fee; generation stops, previews continue. Unused credits roll over up to 2× the monthly budget. Video up to 1920×1080. Images and PDFs up to 16k px.",
  },
  {
    tool: "Cloudinary",
    what: "Image and video platform. Delivery URLs resize, crop, and overlay text on assets you upload.",
    bestFor:
      "Teams that already keep images or video in Cloudinary and want a derived 1200×630 file from that library.",
    generate:
      "Upload, then request a transformation URL. A text overlay uses l_text. There is no documented POST-a-page-URL endpoint that returns a social card. A unique URL counts as one transformation the first time it is generated.",
    mcp: "Remote OAuth servers, including https://asset-management.mcp.cloudinary.com/mcp. That server can build derived assets. Image generation from a text prompt needs the Image Generation add-on. MCP access is listed on every Image & Video API plan, including Free.",
    framework:
      "Upload API and URL delivery. The MCP docs also ship skills for React and Next.js. This is a media SDK, not an og:image meta-tag helper.",
    freeTier:
      "Free plan, no card, no expiry, 25 credits per month, 3 users, 1 product environment. 1 credit = 1,000 transformations, or 1 GB storage, or 1 GB bandwidth.",
    paid: "Plus is $99/month, or $89/month on annual billing, with 225 credits. Advanced is $249/month, or $224/month on annual billing, with 600 credits. From the pricing snapshot sourced from cloudinary.com/pricing.",
    limits:
      "Free max image file size is 10 MB. Admin API is 500 requests/hour on Free and 2,000 on Plus. Transformations and bandwidth use a rolling 30-day window on Free, Plus, and Advanced. Over the credit pool, the account is warned and can later be disabled. Cached repeats of one URL do not add transformations.",
  },
];

const API_SAMPLE = `curl -X POST "https://ogpilot.com/api/v1/images" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json" \\
  -d '{"token":"YOUR_JWT_TOKEN"}'

# Required JWT claims: iss (verified domain), sub (API key prefix).
# Optional: iat, template, title, description, path.
# Sign on your server, or use the Ruby, JavaScript, Python, or PHP SDK.
# Docs: https://ogpilot.com/docs`;

const MCP_SAMPLE = `{
  "mcpServers": {
    "og-pilot": {
      "type": "streamable-http",
      "url": "https://ogpilot.com/mcp"
    }
  }
}

# Auth: OAuth 2.1 with PKCE. No API key in this file.
# Docs: https://ogpilot.com/docs/mcp

generate_og_image
{ "url": "https://example.com/pricing" }`;

const COLUMNS: { key: keyof Omit<ComparisonRow, "tool">; label: string }[] = [
  { key: "what", label: "What it is" },
  { key: "bestFor", label: "Best for" },
  { key: "generate", label: "How you generate an OG image" },
  { key: "mcp", label: "MCP / AI-agent support" },
  { key: "framework", label: "Framework fit" },
  { key: "freeTier", label: "Free tier" },
  { key: "paid", label: "Starting paid price" },
  { key: "limits", label: "Notable limits" },
];

const sectionClass =
  "mt-16 rounded-3xl border border-orange-200/80 bg-white/90 p-6 shadow-xl sm:p-8 dark:border-orange-500/25 dark:bg-slate-900/80 dark:shadow-none";

const linkClass = "font-medium text-orange-700 hover:underline dark:text-orange-300";

function CodeSample({ label, code }: { label: string; code: string }) {
  return (
    <div className="mt-6">
      <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</p>
      <pre className="overflow-x-auto rounded-2xl border border-orange-200 bg-orange-50 p-5 text-sm leading-relaxed text-slate-900 dark:border-orange-500/30 dark:bg-slate-950 dark:text-orange-50">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function BannerbearAlternative({
  page_title: pageTitle = "Bannerbear Alternative for OG Images",
  faqs = [],
}: BannerbearAlternativeProps) {
  return (
    <>
      <Head title={pageTitle} />

      <div className="min-h-screen bg-[radial-gradient(circle_at_0%_10%,#fed7aa_0,transparent_45%),radial-gradient(circle_at_100%_0%,#fecaca_0,transparent_38%),linear-gradient(180deg,#fff7ed_0%,#ffffff_32%,#fffbeb_100%)] pb-20 dark:bg-[radial-gradient(circle_at_0%_10%,#431407_0,transparent_45%),radial-gradient(circle_at_100%_0%,#7f1d1d_0,transparent_38%),linear-gradient(180deg,#0f172a_0%,#111827_32%,#0b1120_100%)]">
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <PublicHeader />

          <section className="relative mt-8 overflow-hidden rounded-[2.25rem] border border-orange-200/80 bg-white/85 p-8 shadow-[0_35px_70px_-40px_rgba(194,65,12,0.55)] backdrop-blur sm:p-10 dark:border-orange-500/30 dark:bg-slate-900/80 dark:shadow-[0_35px_70px_-40px_rgba(249,115,22,0.35)]">
            <div className="relative">
              <Badge className="mb-5 border border-orange-200 bg-orange-100 px-4 py-1.5 tracking-[0.22em] text-orange-700 uppercase dark:border-orange-500/40 dark:bg-orange-500/20 dark:text-orange-100">
                bannerbear alternative
              </Badge>
              <h1 className="max-w-4xl text-4xl leading-tight font-bold text-balance text-slate-900 sm:text-5xl dark:text-slate-100">
                Bannerbear alternative: Placid, Cloudinary, and OG Pilot
              </h1>
              <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-700 sm:text-lg dark:text-slate-300">
                Use this page when you are choosing a hosted tool for an Open Graph image. It compares Bannerbear,
                Placid, Cloudinary, and OG Pilot on how you generate the file, MCP support, the free tier, and the
                starting paid price. Prices are as of October 2026 and only where the vendor publishes them. A cell says
                &quot;not published&quot; when the page we checked is silent.
              </p>
            </div>
          </section>

          <section id="comparison" className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">OG image API comparison</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Bannerbear and Placid are template products: you design a layout, then the API or a URL fills it.
              Cloudinary is a media pipeline: you transform an uploaded asset, including a crop and a text overlay that
              can serve as a cloudinary og image. OG Pilot is a social-card API: named templates, or an agent call with
              a page URL. If you need a placid alternative for the same reason, the decision section is below the table.
            </p>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[88rem] border-collapse text-left text-sm text-slate-700 dark:text-slate-300">
                <thead>
                  <tr className="border-b border-orange-200 dark:border-orange-500/30">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Tool</th>
                    {COLUMNS.map((column) => (
                      <th key={column.key} className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map((row) => (
                    <tr key={row.tool} className="border-b border-orange-100 align-top dark:border-orange-500/20">
                      <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">{row.tool}</th>
                      {COLUMNS.map((column) => (
                        <td key={column.key} className="px-3 py-3">
                          {row[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Checked 2026-10-08.{" "}
              <a
                href="https://www.bannerbear.com/pricing/"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Bannerbear pricing
              </a>
              ,{" "}
              <a
                href="https://developers.bannerbear.com/v5/"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Bannerbear API reference
              </a>
              ,{" "}
              <a href="https://placid.app/pricing" className={linkClass} target="_blank" rel="noopener noreferrer">
                Placid pricing
              </a>
              ,{" "}
              <a
                href="https://placid.app/docs/2.0/rest/images"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Placid REST images
              </a>
              ,{" "}
              <a
                href="https://placid.app/docs/2.0/url/generate-images"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Placid URL API
              </a>
              ,{" "}
              <a href="https://cloudinary.com/pricing" className={linkClass} target="_blank" rel="noopener noreferrer">
                Cloudinary pricing
              </a>
              ,{" "}
              <a
                href="https://cloudinary.com/documentation/pricing.md"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Cloudinary pricing snapshot
              </a>
              , and{" "}
              <a
                href="https://cloudinary.com/documentation/cloudinary_llm_mcp"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Cloudinary MCP
              </a>
              . OG Pilot figures are from this app&apos;s plan catalog, API docs, and MCP guide.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Which should you pick?</h2>
            <div className="mt-4 max-w-3xl space-y-4 text-base leading-7 text-slate-700 dark:text-slate-300">
              <p>
                Pick <strong>OG Pilot</strong> when the job is the social card. You want a 1200×630 URL from your own
                server, or you want an agent to call <code>generate_og_image</code> with a page URL. You do not need to
                draw the layout, and you do not need video or PDF from the same vendor.
              </p>
              <p>
                Pick <strong>Bannerbear</strong> when the layout is a designed template and the same account should also
                render video, PDFs, or multi-step workflows. The signed link Bannerbear calls an Instant URL, and
                describes as a fit for Open Graph images, is listed on Scale at $149/month, not on the $49 Automate
                plan.
              </p>
              <p>
                Pick <strong>Placid</strong> when you want that editor plus a URL you can place in <code>og:image</code>
                , and you may also generate PDFs, video, or images from WordPress, Webflow, Ghost, Make, n8n, or Zapier.
                The public pricing page publishes credit amounts and does not publish a dollar price, so this page does
                not invent one.
              </p>
              <p>
                Pick <strong>Cloudinary</strong> when the image already lives in a media library and the Open Graph file
                is one more derivative: a fill crop, a format, a text overlay, then the CDN. It is the wrong default
                when you only need social cards and do not want to manage uploads, storage, and bandwidth.
              </p>
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">What the published prices imply</h2>
            <div className="mt-4 max-w-3xl space-y-4 text-base leading-7 text-slate-700 dark:text-slate-300">
              <p>These are arithmetic on published prices. They are not latency tests, and they are not a full bill.</p>
              <p>
                Bannerbear Automate is $49 for 1,000 API credits. The pricing FAQ says a jpg at scale 1 is one credit,
                so that render is $49 ÷ 1,000 = $0.049. A jpg and a png at scale 2 is four credits, $0.196. Each AI
                layer adds another credit.
              </p>
              <p>
                OG Pilot&apos;s API and MCP use different meters. Starter is $5/month for up to 10,000 pages and 0 AI
                credits, so a full API quota is $5 ÷ 10,000 = $0.0005 per page, with no extra credits purchased. MCP{" "}
                <code>generate_og_image</code> reserves one AI credit and does not spend that page quota. A trial is not
                enough. Extra credits are $9 ÷ 30 = $0.30 each. Builder is the first plan that includes credits (100 at
                $49/month).
              </p>
              <p>
                Cloudinary Plus is $99/month for 225 credits on the monthly column of the pricing snapshot. One credit
                is 1,000 transformations, so 225 × 1,000 = 225,000 first-time transformation URLs, and $99 ÷ 225,000 =
                $0.00044, only if storage and bandwidth consume none of those credits. A repeat view of the same URL
                does not add a transformation. Placid&apos;s dollar price is not published, so there is no per-image
                dollar figure here. Placid does say 1 image = 1 credit, and Basic includes 500 credits a month.
              </p>
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">OG Pilot API and MCP</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              The request body is the signed token. The documented JSON response includes <code>width</code> 1200 and{" "}
              <code>height</code> 630. Prefer an SDK over hand-rolled signing. Setup is on{" "}
              <Link href="/docs" className={linkClass}>
                /docs
              </Link>
              . The same card from an agent is the remote MCP server.
            </p>
            <CodeSample label="OG Pilot API request" code={API_SAMPLE} />
            <CodeSample label="MCP install" code={MCP_SAMPLE} />
          </section>

          <section className="mt-16 rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-orange-900 p-8 text-white shadow-xl sm:p-10">
            <h2 className="text-3xl font-bold">Get an API key</h2>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-200">
              The 7-day trial does not need a card. Verify the domain, then call the API. To let an agent generate the
              card, install the MCP server on an active plan that has a credit.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                <Link href="/sign_up">
                  Get an API key
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-transparent text-white hover:bg-white/10"
              >
                <Link href="/docs/mcp">Install the MCP server</Link>
              </Button>
            </div>
          </section>

          <ActivationGuide
            title="Related guides"
            links={[
              {
                href: "/og-image-api",
                label: "OG image API",
                detail: "Hosted API next to @vercel/og and a DIY Satori pipeline.",
              },
              {
                href: "/nextjs-og-image-generator",
                label: "Next.js OG images",
                detail: "Metadata file, ImageResponse, or this API from generateMetadata.",
              },
              {
                href: "/og-image-size",
                label: "og:image size",
                detail: "Sourced sizes for Facebook, LinkedIn, WhatsApp, and the rest.",
              },
              {
                href: "/docs/mcp",
                label: "MCP connector",
                detail: "Remote URL, OAuth, and the four tool schemas.",
              },
              {
                href: "/open-graph-debugger",
                label: "Open Graph debugger",
                detail: "Check the tags on a public URL before you trust a share.",
              },
              {
                href: "/new",
                label: "Generate one image",
                detail: "One card in the browser, before you sign requests.",
              },
            ]}
          >
            <p>
              After the URL is live, run it through the{" "}
              <Link href="/open-graph-debugger" className={linkClass}>
                Open Graph debugger
              </Link>
              . The{" "}
              <Link href="/og-image-api" className={linkClass}>
                OG image API comparison
              </Link>{" "}
              covers @vercel/og. The{" "}
              <Link href="/nextjs-og-image-generator" className={linkClass}>
                Next.js guide
              </Link>{" "}
              covers the metadata file. Sizes are on the{" "}
              <Link href="/og-image-size" className={linkClass}>
                cheat sheet
              </Link>
              .
            </p>
          </ActivationGuide>

          {faqs.length > 0 ? (
            <section className={sectionClass}>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Questions</h2>
              <div className="mt-6 space-y-6">
                {faqs.map((faq) => (
                  <article key={faq.question}>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{faq.question}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{faq.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <SiteFooter className="mt-16" showSeparator={false} />
        </div>
      </div>
    </>
  );
}
